import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import type { AccountSummary } from '@/api/accounts'
import { getLoanDetail } from '@/api/loans'
import { listGoals } from '@/api/goals'
import { LoanPaymentModal } from '@/components/loans/LoanModals'
import { ContributeModal } from '@/components/goals/GoalModals'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { ROUTES } from '@/routes/routes'
import { kindInfo } from './addKinds'
import { FormShell, InfoNote } from './fields'

/**
 * EMIs and goal contributions already have their own forms (on Loans and Goals). Here the
 * person only picks which loan or goal, then that form takes over from the add flow.
 */

function PickList({
  label,
  items,
  onPick,
}: {
  label: string
  items: { id: string; title: string; sub: string; amount: string }[]
  onPick: (id: string) => void
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-col gap-1.5">
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--fl-text-3)]">
        {label}
      </p>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onPick(item.id)}
          className="flex items-center gap-3 rounded-xl border border-[var(--fl-border)] px-3 py-2.5 text-left hover:bg-[var(--fl-surface-hover)]"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[14.5px] font-medium text-[var(--fl-text-1)]">
              {item.title}
            </span>
            <span className="block text-xs text-[var(--fl-text-2)]">{item.sub}</span>
          </span>
          <span className="font-serif text-base font-semibold tabular-nums text-[var(--fl-text-1)]">
            {item.amount}
          </span>
        </button>
      ))}
    </div>
  )
}

export function EmiPicker({
  accounts,
  onBack,
  onClose,
  onHandoff,
}: {
  accounts: AccountSummary[]
  onBack?: () => void
  onClose: () => void
  onHandoff: (node: ReactNode) => void
}) {
  const info = kindInfo('emi')
  const { format } = useFormatCurrency()
  const [loanId, setLoanId] = useState<string | null>(null)
  const loans = accounts.filter((a) => a.type === 'LOAN')
  const { data: loan, isError } = useQuery({
    queryKey: ['loan', loanId],
    queryFn: () => getLoanDetail(loanId!),
    enabled: !!loanId,
  })
  useEffect(() => {
    if (loan) onHandoff(<LoanPaymentModal loan={loan} type="EMI" onClose={onClose} />)
  }, [loan, onHandoff, onClose])

  return (
    <FormShell
      title={info.label}
      Icon={info.Icon}
      tone={info.tone}
      onBack={onBack}
      onClose={onClose}
    >
      {loans.length === 0 ? (
        <InfoNote>
          No loans yet.{' '}
          <Link to={ROUTES.APP.LOANS} onClick={onClose} className="font-medium underline">
            Add a loan
          </Link>{' '}
          to record its EMIs.
        </InfoNote>
      ) : (
        <PickList
          label="Which loan"
          onPick={setLoanId}
          items={loans.map((l) => ({
            id: l.id,
            title: l.name,
            sub: 'Outstanding',
            amount: format(Math.abs(l.balance)),
          }))}
        />
      )}
      {loanId && !loan && !isError && <p className="text-sm text-[var(--fl-text-2)]">Opening…</p>}
      {isError && <p className="text-sm text-[var(--fl-error)]">Couldn&rsquo;t open that loan.</p>}
    </FormShell>
  )
}

export function GoalPicker({
  onBack,
  onClose,
  onHandoff,
}: {
  onBack?: () => void
  onClose: () => void
  onHandoff: (node: ReactNode) => void
}) {
  const info = kindInfo('goal')
  const { format } = useFormatCurrency()
  const { data } = useQuery({ queryKey: ['goals'], queryFn: () => listGoals() })
  const goals = data?.active ?? []

  return (
    <FormShell
      title={info.label}
      Icon={info.Icon}
      tone={info.tone}
      onBack={onBack}
      onClose={onClose}
    >
      {data && goals.length === 0 ? (
        <InfoNote>
          No active goals.{' '}
          <Link to={ROUTES.APP.GOALS} onClick={onClose} className="font-medium underline">
            Create a goal
          </Link>{' '}
          to set money aside for it.
        </InfoNote>
      ) : (
        <PickList
          label="Which goal"
          onPick={(id) => {
            const goal = goals.find((g) => g.id === id)
            if (goal) onHandoff(<ContributeModal goal={goal} onClose={onClose} />)
          }}
          items={goals.map((g) => ({
            id: g.id,
            title: g.name,
            sub: `${format(g.currentAmount)} of ${format(g.targetAmount)} · ${g.linkedAccountName}`,
            amount: `${Math.round(g.progressPercent)}%`,
          }))}
        />
      )}
    </FormShell>
  )
}
